import { Link, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { Layers } from 'lucide-react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useTranslation } from '@/hooks/useTranslation';
import { TRACEABILITY } from '@/constants/modules';

type TraceabilityQuickAccessProps = {
  collapsed: boolean;
  onLinkClick?: () => void;
};

/**
 * Prominent traceability shortcut at the top of the left sidebar — bigger
 * (h-7 icon vs the h-5 nav entries) and green-accented (`success`) so it stands out
 * from the muted navigation. Renders nothing without the tracabilite module.
 */
export function TraceabilityQuickAccess({ collapsed, onLinkClick }: TraceabilityQuickAccessProps) {
  const { tokenService } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();

  const modules = (tokenService?.getModules?.() ?? []) as string[];
  if (!modules.includes(TRACEABILITY)) return null;

  const isActive = location.pathname.startsWith('/traceability');

  return (
    <div className="px-2 pb-1">
      <Link
        to="/traceability/scan"
        data-testid="quick-access-traceability"
        onClick={onLinkClick}
        title={t('quickAccess.traceability')}
        aria-current={isActive ? 'page' : undefined}
        className={clsx(
          'flex items-center rounded-xl border py-3 transition-colors',
          'text-success',
          collapsed ? 'justify-center px-0' : 'gap-3 px-3',
          isActive
            ? 'border-success/60 bg-success/20'
            : 'border-success/40 bg-success/10 hover:bg-success/20'
        )}
      >
        <Layers className="h-7 w-7 shrink-0" aria-hidden />
        {!collapsed && (
          <span className="truncate text-sm font-semibold">{t('quickAccess.traceability')}</span>
        )}
      </Link>
    </div>
  );
}
